import { useEffect, useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import type { ReactNode } from 'react';
import { useLogout, useSession } from '@/hooks/api';

const NAV = [
  { to: '/', label: 'Today', icon: '☀️' },
  { to: '/goals', label: 'Goals', icon: '🎯' },
  { to: '/activities', label: 'Activities', icon: '✅' },
  { to: '/notes', label: 'Notes', icon: '📝' },
  { to: '/review', label: 'Review', icon: '📊' },
  { to: '/settings', label: 'Settings', icon: '⚙️' },
];

function useOnline(): boolean {
  const [online, setOnline] = useState(() => navigator.onLine);
  useEffect(() => {
    const on = () => setOnline(true);
    const off = () => setOnline(false);
    window.addEventListener('online', on);
    window.addEventListener('offline', off);
    return () => {
      window.removeEventListener('online', on);
      window.removeEventListener('offline', off);
    };
  }, []);
  return online;
}

export function AppShell({ children }: { children: ReactNode }) {
  const { data } = useSession();
  const logout = useLogout();
  const navigate = useNavigate();
  const online = useOnline();
  const [menuOpen, setMenuOpen] = useState(false);

  const handleLogout = async () => {
    await logout.mutateAsync();
    navigate('/login', { replace: true });
  };

  return (
    <div className="min-h-screen">
      {!online && (
        <div
          role="status"
          className="bg-warning/90 px-4 py-1.5 text-center text-sm font-medium text-slate-900"
        >
          You are offline — changes cannot be saved right now.
        </div>
      )}

      {/* Top bar (mobile) */}
      <header className="sticky top-0 z-40 flex items-center justify-between border-b border-white/5 bg-surface/90 px-4 py-3 backdrop-blur lg:hidden">
        <span className="font-semibold tracking-tight">Otakotakdev</span>
        <button
          type="button"
          className="btn-ghost"
          onClick={() => setMenuOpen((v) => !v)}
          aria-expanded={menuOpen}
          aria-label="Toggle navigation menu"
        >
          ☰
        </button>
      </header>

      <div className="flex">
        {/* Sidebar (desktop) */}
        <aside className="sticky top-0 hidden h-screen w-56 shrink-0 flex-col border-r border-white/5 p-4 lg:flex">
          <div className="mb-8 px-2">
            <h1 className="text-lg font-semibold tracking-tight">Otakotakdev</h1>
            <p className="text-xs text-muted">Second brain</p>
          </div>
          <nav className="flex flex-1 flex-col gap-1" aria-label="Main navigation">
            {NAV.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === '/'}
                className={({ isActive }) =>
                  `flex items-center gap-3 rounded-lg px-3 py-2 text-sm transition-colors ${
                    isActive ? 'bg-panel-2 text-accent' : 'text-muted hover:bg-panel hover:text-ink'
                  }`
                }
              >
                <span aria-hidden>{item.icon}</span>
                {item.label}
              </NavLink>
            ))}
          </nav>
          <div className="border-t border-white/5 pt-4">
            <p className="truncate px-2 text-sm text-ink">{data?.user?.displayName ?? 'User'}</p>
            <button type="button" className="btn-ghost mt-2 w-full" onClick={handleLogout}>
              Log out
            </button>
          </div>
        </aside>

        {/* Mobile nav drawer */}
        {menuOpen && (
          <div className="fixed inset-0 z-50 lg:hidden">
            <div
              className="absolute inset-0 bg-black/60"
              onClick={() => setMenuOpen(false)}
              aria-hidden
            />
            <nav
              className="absolute right-0 top-0 flex h-full w-64 flex-col gap-1 bg-panel p-4"
              aria-label="Mobile navigation"
            >
              <div className="mb-4 flex items-center justify-between">
                <span className="font-semibold">Menu</span>
                <button type="button" className="btn-ghost" onClick={() => setMenuOpen(false)}>
                  ✕
                </button>
              </div>
              {NAV.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.to === '/'}
                  onClick={() => setMenuOpen(false)}
                  className={({ isActive }) =>
                    `flex items-center gap-3 rounded-lg px-3 py-2 text-sm ${
                      isActive ? 'bg-panel-2 text-accent' : 'text-muted'
                    }`
                  }
                >
                  <span aria-hidden>{item.icon}</span>
                  {item.label}
                </NavLink>
              ))}
              <button type="button" className="btn-ghost mt-auto" onClick={handleLogout}>
                Log out
              </button>
            </nav>
          </div>
        )}

        <main className="min-w-0 flex-1 px-4 py-6 lg:px-8">{children}</main>
      </div>
    </div>
  );
}
